import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  createReportSchedule,
  deleteReportSchedule,
  pauseReportSchedule,
  resumeReportSchedule,
  updateReportSchedule,
} from "../services/report-schedule-service";
import {
  CreateNotificationSchedulePayload,
  ScheduleStatus,
  UpdateNotificationSchedulePayload,
} from "../types";
import { REPORT_SCHEDULES_QUERY_KEY } from "./use-report-schedules";

export function useReportScheduleMutations() {
  const [translate] = useTranslation(["insights"]);
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [REPORT_SCHEDULES_QUERY_KEY] });

  const create = useMutation({
    mutationFn: (payload: CreateNotificationSchedulePayload) =>
      createReportSchedule(payload),
    onSuccess: () => {
      invalidate();
      toast.success(translate("schedule-feedback-create-success"));
    },
    onError: () => toast.error(translate("schedule-feedback-create-error")),
  });

  const update = useMutation({
    mutationFn: ({
      id,
      ...payload
    }: UpdateNotificationSchedulePayload & { id: number }) =>
      updateReportSchedule(id, payload),
    onSuccess: () => {
      invalidate();
      toast.success(translate("schedule-feedback-update-success"));
    },
    onError: () => toast.error(translate("schedule-feedback-update-error")),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteReportSchedule(id),
    onSuccess: () => {
      invalidate();
      toast.success(translate("schedule-feedback-delete-success"));
    },
    onError: () => toast.error(translate("schedule-feedback-delete-error")),
  });

  /**
   * Não existe endpoint de toggle: escolhemos pause ou resume conforme o
   * status atual do agendamento.
   */
  const toggle = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ScheduleStatus }) =>
      status === "ACTIVE" ? pauseReportSchedule(id) : resumeReportSchedule(id),
    onSuccess: () => invalidate(),
    onError: () => toast.error(translate("schedule-feedback-toggle-error")),
  });

  return { create, update, remove, toggle };
}
